import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CircleNotch, MagnifyingGlass } from 'phosphor-react';
import { supabase } from '@/lib/supabase';
import { useActiveCenter } from '@/hooks/useActiveCenter';
import { EventCard } from '@/components/EventCard';
import { Chip } from '@/components/ui/Chip';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import type { EventSearchRow } from '@/types/database';

const PAGE = 30;            // taille de page du scroll infini
const CANDIDATE_CAP = 500;  // plafond de candidats côté RPC (search_events_fts)

const RADIUS_OPTIONS: Array<{ label: string; km: number | null }> = [
  { label: 'Partout', km: null },
  { label: '10 km', km: 10 },
  { label: '25 km', km: 25 },
  { label: '50 km', km: 50 },
  { label: '100 km', km: 100 },
];

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function Search() {
  const nav = useNavigate();
  const center = useActiveCenter();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 300);
  const [radiusKm, setRadiusKm] = useState<number | null>(null); // « Partout » par défaut → aucun filtre, tri par distance
  const [results, setResults] = useState<EventSearchRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [searched, setSearched] = useState(false);

  const offsetRef = useRef(0);
  const reqIdRef = useRef(0);          // ignore les réponses obsolètes (course)
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const fetchPage = useCallback(async (reset: boolean) => {
    const q = debouncedQuery.trim();
    if (q.length < 2) {
      setResults([]);
      setSearched(false);
      setHasMore(false);
      return;
    }
    const offset = reset ? 0 : offsetRef.current;
    const reqId = reset ? ++reqIdRef.current : reqIdRef.current;
    if (reset) setLoading(true); else setLoadingMore(true);

    const { data, error } = await supabase.rpc('search_events_fts', {
      p_query: q,
      p_lat: center.lat,
      p_lon: center.lon,
      p_radius_km: radiusKm,
      p_limit: PAGE,
      p_offset: offset,
    });

    if (reqId !== reqIdRef.current) return; // une recherche plus récente a pris le relais
    if (error) console.error(error);
    const rows = (data as EventSearchRow[]) ?? [];
    offsetRef.current = offset + rows.length;
    setResults(prev => (reset ? rows : [...prev, ...rows]));
    setHasMore(rows.length === PAGE && offsetRef.current < CANDIDATE_CAP);
    setSearched(true);
    setLoading(false);
    setLoadingMore(false);
  }, [debouncedQuery, radiusKm, center.lat, center.lon]);

  // (Re)lance la recherche quand le terme, le rayon ou le centre changent.
  useEffect(() => { fetchPage(true); }, [fetchPage]);

  // Scroll infini : charge la page suivante quand la sentinelle devient visible.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && !loading && !loadingMore) {
        fetchPage(false);
      }
    }, { rootMargin: '400px' });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loading, loadingMore, fetchPage]);

  const trimmed = debouncedQuery.trim();
  // Spinner dès la frappe (debounce en attente) puis pendant la requête.
  const searching = query.trim().length >= 2 && (loading || query !== debouncedQuery);

  return (
    <div className="bg-paper text-ink-1 min-h-screen">
      <header className="sticky top-0 z-20 bg-paper px-4 pt-[max(env(safe-area-inset-top),12px)] pb-3 border-b border-paper-edge">
        <div className="flex items-center gap-3">
          <button
            onClick={() => nav(-1)}
            className="h-9 w-9 shrink-0 grid place-items-center rounded-full bg-paper-elev ring-1 ring-paper-edge"
            aria-label="Retour"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="relative flex-1">
            <MagnifyingGlass
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3 pointer-events-none"
            />
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Nom d'événement, ville…"
              className="w-full h-9 pl-9 pr-9 rounded-full bg-paper-elev ring-1 ring-paper-edge text-[14px] placeholder:text-ink-3 outline-none focus:ring-2 focus:ring-ink-2"
            />
            {searching && (
              <CircleNotch
                size={16}
                weight="bold"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-2 animate-spin"
                aria-label="Recherche en cours"
              />
            )}
          </div>
        </div>

        {/* Unique filtre : la distance autour de « {label du centre} » */}
        <div className="mt-3 flex items-center gap-2 overflow-x-auto scrollbar-none">
          {RADIUS_OPTIONS.map(opt => (
            <Chip
              key={opt.label}
              active={radiusKm === opt.km}
              onClick={() => setRadiusKm(opt.km)}
            >
              {opt.label}
            </Chip>
          ))}
        </div>
      </header>

      <div className="px-5 pt-5 pb-24">
        {loading && (
          <div className="flex flex-col gap-4">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-md" />)}
          </div>
        )}

        {!loading && searched && results.length === 0 && (
          <EmptyState
            icon={<MagnifyingGlass size={28} />}
            title="Aucun résultat"
            body={`Aucun événement trouvé pour « ${trimmed} »${radiusKm ? ` à moins de ${radiusKm} km` : ''}.`}
          />
        )}

        {!loading && results.length > 0 && (
          <>
            <div className="flex flex-col gap-6">
              {results.map(e => (
                <EventCard key={e.id} event={e} variant="row" />
              ))}
            </div>
            <div ref={sentinelRef} className="h-10" aria-hidden="true" />
            {loadingMore && (
              <div className="flex justify-center py-4">
                <span className="h-6 w-6 animate-spin rounded-full border-2 border-paper-edge border-t-ink-2" />
              </div>
            )}
          </>
        )}

        {!loading && !searched && (
          <p className="t-cap text-ink-3 text-center mt-16">
            Tapez au moins 2 caractères pour lancer la recherche.
          </p>
        )}
      </div>
    </div>
  );
}
